/**
 * What to write to the access document, given the shape it is already in.
 *
 * WHY THIS IS NOT JUST `{ emails, admins }`
 *
 * There are two shapes in the wild. The current one keeps a list of admins; the
 * older one keeps a map of everyone's role and marks admins inside it. The rules
 * accept either — but never a mixture, and never one with the writer missing
 * from the admins, because that is what stops the last admin locking everyone
 * out of the board.
 *
 * The app only ever wrote the current shape. On a board still using the older
 * one it read `admins`, found no such field, and sent an EMPTY list — which
 * cannot contain the person writing it. Every attempt to add somebody was
 * refused, from the setup wizard and the People screen alike, with no message
 * at all. That board had never been able to add anybody.
 *
 * So: keep the shape the document is in. A board on the older format goes on
 * working without its data being touched, and one that has moved stays moved.
 *
 * THE ONE THING THE OLD SHAPE CANNOT DO
 *
 * Changing who is an admin means changing the role map itself, and a write that
 * carried both a map and an admins list would be refused by the rules. Rather
 * than silently ignore such a change — the failure that started all this — that
 * case throws, and says what to run.
 */

export interface AccessDoc {
  emails: string[];
  admins?: string[];
  roles?: Record<string, string>;
}

/** Everyone the older map marks as an admin. */
export function adminsFromRoles(roles: Record<string, string> | undefined): string[] {
  return Object.entries(roles ?? {})
    .filter(([, r]) => r === 'admin')
    .map(([e]) => e.toLowerCase());
}

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

/**
 * @param emails  who should be on the board after this write
 * @param admins  who should be an admin after this write
 * @param legacy  the role map currently on the document, if it still has one
 * @throws when the older shape is in use and the admins are being changed
 */
export function accessWrite(
  emails: string[],
  admins: string[],
  legacy: Record<string, string> | undefined
): AccessDoc {
  const legacyAdmins = adminsFromRoles(legacy);
  if (legacyAdmins.length === 0) return { emails, admins };

  if (!sameSet(admins.map((a) => a.toLowerCase()), legacyAdmins)) {
    throw new Error(
      'This board still keeps its permissions in the older format, which cannot ' +
        'record a change of admin. Everything else — adding and removing people — ' +
        'works as it is. To change admins, the format has to be moved on first: ' +
        'node scripts/migrate-access.mjs --apply'
    );
  }
  // Untouched map, new list of people. The rules accept this, and nothing about
  // anybody's existing role changes.
  return { emails, roles: legacy };
}
